import { api } from "@/api/client";
import { unwrap } from "@/api/errors";
import type {
  Assistant,
  AssistantCategory,
  AssistantDetail,
  AssistantSort,
  ListAssistantsResponse,
} from "../types";

export interface ListAssistantsParams {
  search?: string;
  category?: string;
  sort?: AssistantSort;
  limit?: number;
  offset?: number;
}

/** GET /v1/assistants — the public, listed catalogue (filterable + sortable). */
export async function listAssistants(
  params: ListAssistantsParams = {},
): Promise<ListAssistantsResponse> {
  return unwrap(
    await api.GET("/v1/assistants", { params: { query: params } }),
  ) as ListAssistantsResponse;
}

/** GET /v1/assistants/categories — the category taxonomy (rarely changes). */
export async function listCategories(): Promise<AssistantCategory[]> {
  const res = unwrap(await api.GET("/v1/assistants/categories", {})) as {
    data?: AssistantCategory[];
  };
  return res.data ?? [];
}

/** GET /v1/assistants/{id} — one assistant plus "more by this creator". */
export async function getAssistant(id: string): Promise<AssistantDetail> {
  return unwrap(
    await api.GET("/v1/assistants/{id}", { params: { path: { id } } }),
  ) as AssistantDetail;
}

/** GET /v1/assistants/me — the signed-in user's own assistants. */
export async function listMyAssistants(): Promise<Assistant[]> {
  const res = unwrap(await api.GET("/v1/assistants/me", {})) as { data?: Assistant[] };
  return res.data ?? [];
}

/** GET /v1/assistants/pinned — the user's sidebar-pinned assistants. */
export async function listPinnedAssistants(): Promise<Assistant[]> {
  const res = unwrap(await api.GET("/v1/assistants/pinned", {})) as { items?: Assistant[] };
  return res.items ?? [];
}

/** GET /v1/assistants/{id}/rate — the user's own rating (0 = none). */
export async function getMyRating(id: string): Promise<number> {
  const res = unwrap(
    await api.GET("/v1/assistants/{id}/rate", { params: { path: { id } } }),
  ) as { rating?: number };
  return res.rating ?? 0;
}
